import React from "react"
import PropTypes from "prop-types"
import moment from "moment"
import "app/static/stylus/reusable_components/Timeline.styl"
import { parseDateToMDYY } from "shared/imports/sharedHelperFunctions"
import Icon from "components/Icon"
import SegueLink from "app/static/frontend/middleware/SegueLink"
import Tooltip from "components/Tooltip"

import { Timeline as TimelineDS } from "quorumdesign"

const { CongressChamber, LegislativeBody } = DjangIO.app.models

/**
 * Returns an object with the chamber props for an event entry on the design system timeline component
 * based on the value of the chamber
 *
 * @param {Number} chamber - chamber from the event
 * @param {Number} legislative_body - legislative body value
 * @returns {Object} Object to be spread into the event object in the events array of a datedEvent object
 */
export const getChamberIcon = ({ chamber, legislative_body }) => {
    if (legislative_body) {
        const letter = LegislativeBody.items().find((x) => x.value === legislative_body)?.letter
        return letter ? { circleText: letter } : { icon: "university" }
    }
    return !chamber || chamber === CongressChamber.both.value
        ? { icon: "university" }
        : { circleText: CongressChamber.items().find((x) => x.value === chamber).label[0] }
}

/**
 * Get the datedEvent object for the design system timeline component from
 * a given timeline event
 * @param {Object} timelineItem - object from the timeline prop's array of events
 * @returns {Object} datedEventObject to be passed to the design system timeline component
 */
export const getDatedEventObjFromTimelineItem = (timelineItem) => {
    const date = parseDateToMDYY(timelineItem.acted_at, Userdata.has_international_region)

    const events = []

    timelineItem.events &&
        timelineItem.events.forEach((event) => {
            event.events &&
                event.events.forEach((innerEvent) => {
                    const eventPropsFromChamber = getChamberIcon(innerEvent)

                    events.push({
                        text: innerEvent.text,
                        ...eventPropsFromChamber,
                    })
                })
        })

    return { date, events }
}

export const Timeline = ({ timeline }) => {
    const getTotalEventCount = () =>
        timeline &&
        timeline.reduce((acc, curr) => curr.events.reduce((accIn, currIn) => currIn.events.length + accIn, 0) + acc, 0)

    const getChamber = ({ chamber, legislative_body }) => {
        let chamberContent
        let [columnClasses, circleClasses, chamberClasses] = ["chamber-col", "", ""]

        if (Userdata.isOnlyEUMode()) {
            columnClasses += " eu"
            chamberClasses += "eu"
            chamberContent =
                !legislative_body || legislative_body === LegislativeBody.eu_unknown.value ? (
                    <Icon name="fa-bank" />
                ) : (
                    LegislativeBody.items().find((x) => x.value === legislative_body).abbrev
                )
        } else if (legislative_body) {
            chamberContent = LegislativeBody.items().find((x) => x.value === legislative_body)?.letter || (
                <Icon name="fa-bank" />
            )
        } else {
            circleClasses += "circle"
            chamberContent =
                !chamber || chamber === CongressChamber.both.value ? (
                    <Icon name="fa-bank" />
                ) : (
                    CongressChamber.items().find((x) => x.value === chamber).label[0]
                )
        }

        return (
            <div className={columnClasses}>
                <div className={circleClasses}>
                    <h4 className={chamberClasses}>{chamberContent}</h4>
                </div>
            </div>
        )
    }

    const renderDocument = ({ title, search_url }) =>
        search_url &&
        title && (
            <Tooltip label={title}>
                <SegueLink className="document-link" to={search_url.replace("https://www.quorum.us", "")}>
                    <span>Related Document:</span> {title}
                </SegueLink>
            </Tooltip>
        )

    const renderEvent = ({ text, documents }) => (
        <li>
            {text}
            <div className="documents-container">{documents && documents.map(renderDocument)}</div>
        </li>
    )

    const renderChamber = (event, index) => (
        <div className={`chamber-row${index === 0 ? "" : " border"}`}>
            {/* The Circle */}
            {getChamber(event)}

            {/* The Events */}
            <div className="events">
                <ul>{event.events && event.events.map(renderEvent)}</ul>
            </div>
        </div>
    )

    let hasRenderedFutureDivider = false
    const renderFutureDivider = ({ acted_at, index }) => {
        // if the future divider has not been rendered
        // and the date is before today
        if (!hasRenderedFutureDivider && moment(acted_at).isBefore(moment.now())) {
            hasRenderedFutureDivider = true
            // if it is the first instance, if means that all the events are
            // in the past so we don't want to render the divider
            return index !== 0 && <hr className="timeline-divider" />
        }
    }

    const renderDate = ({ acted_at, events, index }) => (
        <div>
            {renderFutureDivider({ acted_at, index })}
            <div className="timeline-event">
                {/* Header */}
                <h2>{parseDateToMDYY(acted_at, Userdata.has_international_region)}</h2>
                {events && events.map(renderChamber)}
            </div>
        </div>
    )

    return (
        <TimelineDS
            emptyText="No events have occurred yet. When an event occurs, it'll show up here."
            title="Timeline"
            datedEvents={timeline && timeline.map((item) => getDatedEventObjFromTimelineItem(item))}
        />
    )
}

Timeline.propTypes = {
    timeline: PropTypes.array,
}

export default Timeline
